import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLAIM_FAMILIES, CLAIM_STATUS, formatEUR, formatDate } from "@/lib/dict";
import { Plus, Search, ShieldAlert, FileText, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import AuthImage from "@/components/AuthImage";
import CommentsPanel from "@/components/CommentsPanel";
import { exportClaimReport } from "@/lib/pdfExport";
import { exportToCsv } from "@/lib/csvExport";
import SavedViews from "@/components/SavedViews";
import ContextualHelpLink from "@/components/ContextualHelpLink";
import { usePickLists } from "@/hooks/usePickLists";

export default function Claims() {
  const qc = useQueryClient();
  const pl = usePickLists();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ client_id: "all", legal_entity_id: "all", asset_id: "all" });
  const [activeViewName, setActiveViewName] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", asset_id: "", claim_family: "multirisk", claim_type: "water_damage", insurer: "", estimated_damage_amount: 0, status: "draft" });

  const { data: claims = [] } = useQuery({ queryKey: ["claims"], queryFn: () => api.get("/claims").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: legalEntities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });

  const assetMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);
  const legalEntitiesForClient = useMemo(() => (
    filters.client_id === "all" ? legalEntities : legalEntities.filter((le) => le.client_id === filters.client_id)
  ), [legalEntities, filters.client_id]);
  const assetsForScope = useMemo(() => assets.filter((a) => {
    if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
    if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
    return true;
  }), [assets, filters.client_id, filters.legal_entity_id]);

  const filtered = useMemo(() => claims.filter((c) => {
    if (tab !== "all" && c.claim_family !== tab) return false;
    if (filters.asset_id !== "all") {
      if (c.asset_id !== filters.asset_id) return false;
    } else if (filters.legal_entity_id !== "all" || filters.client_id !== "all") {
      const a = assetMap[c.asset_id];
      if (!a) return false;
      if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
      if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
    }
    if (search && !`${c.reference} ${c.title}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [claims, tab, search, filters, assetMap]);

  const create = useMutation({
    mutationFn: (d) => api.post("/claims", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["claims"] }); toast.success("Sinistre créé"); setOpen(false); },
  });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Sinistres &amp; Dommage-Ouvrage</h1>
          <p className="text-sm text-slate-500 mt-1.5">{filtered.length} sinistre(s) — séparation DO / Multirisque</p>
          <div className="mt-1.5"><ContextualHelpLink page="claims" /></div>
        </div>
        <div className="flex gap-2">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-claim-btn" className="text-white" style={{ background: "#023E73" }}><Plus size={16} className="mr-1.5" /> Nouveau sinistre</Button>
          </DialogTrigger>          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Créer un sinistre</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Famille</Label>
                <Select value={form.claim_family} onValueChange={(v) => setForm({ ...form, claim_family: v })}>
                  <SelectTrigger data-testid="claim-family"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pl.options("claim.claim_family", CLAIM_FAMILIES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Titre</Label><Input data-testid="claim-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
              <div><Label>Actif</Label>
                <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                  <SelectTrigger data-testid="claim-asset"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><Input value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} /></div>
                <div><Label>Montant estimé €</Label><Input type="number" value={form.estimated_damage_amount} onChange={(e) => setForm({ ...form, estimated_damage_amount: Number(e.target.value) })} /></div>
              </div>
              <div><Label>Assureur</Label><Input value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="claim-submit" onClick={() => create.mutate(form)} className="text-white" style={{ background: "#023E73" }}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button
          data-testid="export-claims-csv" variant="outline"
          onClick={() => exportToCsv("sinistres.csv", filtered, [
            { key: "reference", label: "Référence" },
            { key: "claim_family", label: "Famille", transform: (r) => pl.labelOf("claim.claim_family", r.claim_family, CLAIM_FAMILIES) },
            { key: "claim_type", label: "Type" },
            { key: "title", label: "Titre" },
            { key: "asset", label: "Actif", transform: (r) => assetMap[r.asset_id]?.name },
            { key: "insurer", label: "Assureur" },
            { key: "broker", label: "Courtier" },
            { key: "status", label: "Statut", transform: (r) => CLAIM_STATUS[r.status] || r.status },
            { key: "estimated_damage_amount", label: "Montant estimé €" },
            { key: "expert_assessed_amount", label: "Éval. expert €" },
            { key: "compensation_amount", label: "Indemnisation €" },
            { key: "remaining_cost", label: "Reste à charge €" },
            { key: "date_of_occurrence", label: "Survenance" },
          ])}
        ><Download size={14} className="mr-1.5" /> Export CSV</Button>
        <SavedViews
          page="claims"
          currentFilters={{ tab, search, client_id: filters.client_id, legal_entity_id: filters.legal_entity_id, asset_id: filters.asset_id }}
          activeName={activeViewName}
          onApply={(f, name) => {
            setTab(f.tab || "all");
            setSearch(f.search || "");
            setFilters({
              client_id: f.client_id || "all",
              legal_entity_id: f.legal_entity_id || "all",
              asset_id: f.asset_id || "all",
            });
            setActiveViewName(name);
          }}
          onClearActive={() => setActiveViewName(null)}
        />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="all" data-testid="claim-tab-all">Tous</TabsTrigger>
          <TabsTrigger value="DO" data-testid="claim-tab-do">Dommages-Ouvrage</TabsTrigger>
          <TabsTrigger value="multirisk" data-testid="claim-tab-multirisk">Multirisque</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-3 border-slate-200">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="claims-search" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
          </div>
          <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v, legal_entity_id: "all", asset_id: "all" })}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.legal_entity_id} onValueChange={(v) => setFilters({ ...filters, legal_entity_id: v, asset_id: "all" })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-legal-entity"><SelectValue placeholder="Entité juridique" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes entités</SelectItem>
              {legalEntitiesForClient.map((le) => <SelectItem key={le.id} value={le.id}>{le.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.asset_id} onValueChange={(v) => setFilters({ ...filters, asset_id: v })}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="filter-asset"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous sites</SelectItem>
              {assetsForScope.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filters.client_id !== "all" || filters.legal_entity_id !== "all" || filters.asset_id !== "all" || search) && (
            <Button
              data-testid="reset-filters"
              variant="ghost" size="sm" className="h-9 text-xs text-slate-600"
              onClick={() => { setFilters({ client_id: "all", legal_entity_id: "all", asset_id: "all" }); setSearch(""); }}
            >Réinitialiser</Button>
          )}
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Référence</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Famille</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Titre</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actif</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Assureur</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Statut</th>
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Montant</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const fam = CLAIM_FAMILIES[c.claim_family];
                return (
                  <tr key={c.id} onClick={() => setSelected(c)} data-testid={`claim-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{c.reference}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${fam?.color}`}>{pl.labelOf("claim.claim_family", c.claim_family, CLAIM_FAMILIES)}</span></td>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.title}</td>
                    <td className="px-4 py-3 text-slate-600">{assetMap[c.asset_id]?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.insurer || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">{CLAIM_STATUS[c.status] || c.status}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{formatEUR(c.estimated_damage_amount)}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-10">Aucun sinistre</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="sm:max-w-[560px] overflow-y-auto">
          {selected && <ClaimDetail claim={selected} asset={assetMap[selected.asset_id]} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ClaimDetail({ claim, asset }) {
  const pl = usePickLists();
  const qc = useQueryClient();
  const { data: docs = [] } = useQuery({
    queryKey: ["docs", "claim", claim.id],
    queryFn: () => api.get(`/documents?claim_id=${claim.id}`).then((r) => r.data),
  });
  const fam = CLAIM_FAMILIES[claim.claim_family];
  return (
    <div>
      <SheetHeader>
        <div className="flex items-center justify-between">
          <div className="font-mono text-xs text-slate-500">{claim.reference}</div>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${fam?.color}`}>{pl.labelOf("claim.claim_family", claim.claim_family, CLAIM_FAMILIES)}</span>
        </div>
        <SheetTitle className="font-display text-xl">{claim.title}</SheetTitle>
        <div className="text-xs text-slate-700 mt-1">{CLAIM_STATUS[claim.status] || claim.status}</div>
        <Button
          data-testid="export-claim-report"
          variant="outline" size="sm" className="mt-3 w-fit"
          onClick={async () => {
            const cmts = await api.get(`/claims/${claim.id}/comments`).then((r) => r.data).catch(() => []);
            exportClaimReport(claim, asset, cmts);
            toast.success("Rapport PDF téléchargé");
          }}
        ><Download size={13} className="mr-1.5" /> Exporter rapport PDF</Button>
      </SheetHeader>
      <div className="mt-6 space-y-5 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Description</div>
          <p className="text-slate-700">{claim.description || "—"}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><div className="text-slate-500">Actif</div><div className="font-semibold text-slate-900">{asset?.name}</div></div>
          <div><div className="text-slate-500">Type</div><div className="font-semibold">{claim.claim_type}</div></div>
          <div><div className="text-slate-500">Assureur</div><div className="font-semibold">{claim.insurer || "—"}</div></div>
          <div><div className="text-slate-500">Courtier</div><div className="font-semibold">{claim.broker || "—"}</div></div>
          <div><div className="text-slate-500">Survenance</div><div className="font-semibold">{formatDate(claim.date_of_occurrence)}</div></div>
          <div><div className="text-slate-500">Déclaré le</div><div className="font-semibold">{formatDate(claim.date_reported)}</div></div>
          <div><div className="text-slate-500">Montant estimé</div><div className="font-semibold font-mono">{formatEUR(claim.estimated_damage_amount)}</div></div>
          <div><div className="text-slate-500">Indemnisation</div><div className="font-semibold font-mono">{formatEUR(claim.compensation_amount)}</div></div>
          <div><div className="text-slate-500">Reste à charge</div><div className="font-semibold font-mono">{formatEUR(claim.remaining_cost)}</div></div>
        </div>
        {claim.next_action && (
          <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-900">
            <span className="font-semibold">Prochaine action :</span> {claim.next_action}
          </div>
        )}
        <div className="border-t border-slate-100 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Photos &amp; rapports ({docs.length})</div>
          <FileUploader
            claimId={claim.id}
            assetId={claim.asset_id}
            onUploaded={() => qc.invalidateQueries({ queryKey: ["docs", "claim", claim.id] })}
          />
          {docs.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {docs.map((d) => (
                <div key={d.id} data-testid={`claim-doc-${d.id}`} className="relative aspect-square rounded-md border border-slate-200 overflow-hidden bg-slate-50 group">
                  {d.mime_type?.startsWith("image") ? (
                    <AuthImage src={d.url} alt={d.name} className="w-full h-full object-cover" fallback={<div className="p-2 text-[10px] text-slate-400">{d.name}</div>} />
                  ) : (
                    <div className="p-2 flex flex-col items-center justify-center h-full text-slate-500">
                      <FileText size={20} /><div className="text-[10px] mt-1 text-center line-clamp-2">{d.name}</div>
                    </div>
                  )}
                  <button
                    data-testid={`delete-claim-doc-${d.id}`}
                    onClick={async () => { await api.delete(`/documents/${d.id}`); qc.invalidateQueries({ queryKey: ["docs", "claim", claim.id] }); toast.success("Document supprimé"); }}
                    className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition shadow"
                  ><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="border-t border-slate-100 pt-4">
          <CommentsPanel entityType="claim" entityId={claim.id} />
        </div>
      </div>
    </div>
  );
}
